import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useWebinarT } from '@/hooks/useWebinarT';
import { Skeleton } from '@/components/ui/skeleton';
import { Document, Page } from 'react-pdf';
import PdfSeamlessPage from '@/components/common/asset/PdfSeamlessPage';
import { ArrowLeft, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import type { TriggerEventInput } from '@/features/live/types';
import type { WebinarSlide } from '@/features/webinar/types';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { listWebinarSlideNotes } from '@/features/webinar/webinarThunks';
import { clearPublicSlide } from '@/features/live/liveSlice';
import { useChannelName } from '@/routes/AblyAuth';
import { useChannel } from 'ably/react';
import { getStoredAdminPdfPage, setStoredAdminPdfPage } from '@/lib/liveAdminPdfPageMemory';
import { toSlidesProxyUrl } from '@/lib/slidesProxyUrl';
import { pctRectStyle, scaleCamLayoutPx, type CamLayoutRects } from '@/lib/camLayouts';
import { fitBox, useAreaSize } from '@/lib/useFittedBox';

/** Gouttière des contrôles cam rendus hors cadre (bouton 36px + 8px d'écart), comme la régie. */
const STAGE_CAM_CONTROLS_GUTTER_PX = 44;

// Simple limiteur de débit des événements de page (back + push de layout LiveKit) : le rendu étant
// désormais sans coupure, ce délai doit rester imperceptible pour ne pas brider les slides à effets.
const PDF_PAGE_NAV_COOLDOWN_MS = 150;
export default function PdfLiveView({
  handleTriggerEvent,
  workspaceId,
  webinarId,
  sessionId,
  hasActiveTab,
  isSpeaker,
  isVisible = true,
  fillStage,
  stageRects,
  renderStageCameras,
}: {
  handleTriggerEvent?: (event: TriggerEventInput) => void;
  workspaceId?: number;
  webinarId?: number;
  sessionId?: number;
  hasActiveTab?: boolean;
  isSpeaker?: boolean;
  /** False when another asset (screen share, video, etc.) covers the PDF — keeps it mounted without reloading. */
  isVisible?: boolean;
  /** Aperçu WYSIWYG : retire la marge propre (arrondi 20px) et insère la page pour la colonne cam. */
  fillStage?: boolean;
  /** Aperçu WYSIWYG : boîte de la page + rectangles caméras, même géométrie que le compositor. */
  stageRects?: CamLayoutRects;
  /** Vignettes caméras à poser sur le cadre de la page (rayon mis à l'échelle de sa hauteur). */
  renderStageCameras?: (frameHeightPx: number | undefined) => ReactNode;
}) {
  const { t } = useWebinarT('webinars');
  const dispatch = useAppDispatch();

  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isNavBarVisible, setIsNavBarVisible] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  // Aperçu WYSIWYG : boîte page ajustée au ratio de la slide (comme le compositor) → un PDF
  // carré/portrait gagne en hauteur, l'arrondi reste collé.
  const [pdfPageAspect, setPdfPageAspect] = useState<number | null>(null);
  const { ref: pdfAreaRef, size: pdfArea } = useAreaSize<HTMLDivElement>();
  const pdfFittedBox = pdfPageAspect != null ? fitBox(pdfArea.w, pdfArea.h, pdfPageAspect) : null;
  // Ce calque a son propre cadre, plus court que la scène (barre de slides + boutons en dessous) :
  // on mesure la zone et on y cale le plus grand 16:9 exact. `h-full aspect-[16/9] max-w-full`
  // seul rendait un cadre écrasé quand la largeur limite, et les rects en % s'y déformaient.
  const { ref: stageAreaRef, size: stageArea } = useAreaSize<HTMLDivElement>();

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateWidth = () => {
      const width = el.getBoundingClientRect().width;
      setContainerWidth(width);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const availableWidth = containerWidth > 0 ? containerWidth : 1000;
  const pdfPageWidth = hasActiveTab
    ? Math.min(availableWidth, 1200)
    : Math.min(availableWidth, 1400);
  const pdfContainerWidth = hasActiveTab ? Math.min(availableWidth, 1400) : null;

  const adminResourceStates = useAppSelector((state: RootState) => state.live.adminResourceStates);
  const publicResourceStates = useAppSelector(
    (state: RootState) => state.live.publicResourceStates,
  );
  const visualIdentity = useAppSelector(
    (state: RootState) => state.workspace.currentWorkspace?.visualIdentity,
  );

  const { webinarSlides, webinarSlideNotes } = useAppSelector((state: RootState) => state.webinar);

  const { publicSlide } = useAppSelector((state: RootState) => state.live);

  // L'invité n'a pas accès à `adminResourceStates` : on lit l'état public de la session
  // pour retrouver la slide live et surtout sa page courante (`lastStatePayload.pageNumber`).
  const liveSlideResource = (isSpeaker ? publicResourceStates : adminResourceStates).find(
    (state) => state.resourceType === 'WEBINAR_SLIDE' && state.live,
  );

  const activeLiveSlide = isSpeaker
    ? publicSlide
    : webinarSlides.find((slide) => slide.id === liveSlideResource?.resourceId);

  const cachedLiveSlideRef = useRef<WebinarSlide | null>(null);
  useEffect(() => {
    if (activeLiveSlide) {
      cachedLiveSlideRef.current = activeLiveSlide;
    }
  }, [activeLiveSlide]);

  const displaySlide = activeLiveSlide ?? cachedLiveSlideRef.current;

  const channelName = useChannelName();

  useChannel(channelName!, (message) => {
    if (!isSpeaker || !channelName || !message) return;

    // Les events peuvent arriver à l'unité (`live-event`) ou groupés (`batch-events`),
    // comme côté spectateur/présentateur. On normalise pour traiter chaque event individuellement.
    const events =
      message.name === 'batch-events' && Array.isArray(message.data)
        ? (message.data as Array<{ data?: unknown }>).map((item) => item?.data)
        : [message.data];

    const parsePageNumber = (payloadJson: unknown): number | null => {
      try {
        const payload = typeof payloadJson === 'string' ? JSON.parse(payloadJson) : payloadJson;
        if (
          payload &&
          typeof payload === 'object' &&
          typeof (payload as { pageNumber?: unknown }).pageNumber === 'number'
        ) {
          return (payload as { pageNumber: number }).pageNumber;
        }
      } catch {
        // payload illisible : on ignore
      }
      return null;
    };

    for (const data of events) {
      if (!data || typeof data !== 'object') continue;
      const ev = data as {
        resourceType?: string;
        eventType?: string;
        resourceId?: unknown;
        payloadJson?: unknown;
      };
      if (ev.resourceType !== 'WEBINAR_SLIDE') continue;

      // `resourceId` peut être un nombre ou une chaîne selon l'event back.
      const eventSlideId =
        typeof ev.resourceId === 'number' && Number.isFinite(ev.resourceId)
          ? ev.resourceId
          : typeof ev.resourceId === 'string' && /^\d+$/.test(ev.resourceId.trim())
            ? Number.parseInt(ev.resourceId.trim(), 10)
            : null;

      if (ev.eventType === 'PDF_PAGE_CHANGE') {
        // On ne suit la navigation de page que pour la slide actuellement affichée.
        if (!publicSlide || eventSlideId !== publicSlide.id) continue;
        const pageNumber = parsePageNumber(ev.payloadJson);
        if (pageNumber != null) {
          setCurrentPage(pageNumber);
        }
      } else if (ev.eventType === 'RESOURCE_SHOW') {
        // Affichage d'une slide : on se cale sur la page fournie, sinon la première.
        setCurrentPage(parsePageNumber(ev.payloadJson) ?? 1);
      } else if (ev.eventType === 'RESOURCE_HIDE') {
        if (!eventSlideId || publicSlide?.id === eventSlideId) {
          dispatch(clearPublicSlide());
        }
      }
    }
  });

  useEffect(() => {
    if (!liveSlideResource) return;
    const raw = liveSlideResource.lastStatePayload;
    if (raw) {
      try {
        const payload = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (typeof payload?.pageNumber === 'number') {
          setCurrentPage(payload.pageNumber);
          // Mémoire locale réservée au présentateur (l'invité ne pilote pas).
          if (!isSpeaker && sessionId !== undefined) {
            setStoredAdminPdfPage(sessionId, liveSlideResource.resourceId, payload.pageNumber);
          }
          return;
        }
      } catch {}
    }
    const fallback =
      isSpeaker || sessionId === undefined
        ? 1
        : (getStoredAdminPdfPage(sessionId, liveSlideResource.resourceId) ?? 1);
    setCurrentPage(fallback);
  }, [liveSlideResource?.resourceId, liveSlideResource?.lastStatePayload, sessionId, isSpeaker]);

  useEffect(() => {
    if (!isVisible || !workspaceId || !webinarId || !displaySlide?.id) return;
    dispatch(listWebinarSlideNotes({ workspaceId, webinarId, slideId: displaySlide.id }));
  }, [dispatch, workspaceId, webinarId, displaySlide?.id, isVisible]);

  const currentPageNote = webinarSlideNotes.find((note) => note.pageNumber === currentPage);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const pageNavCooldownRef = useRef(false);
  const pageNavCooldownTimerRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (pageNavCooldownTimerRef.current != null) {
        globalThis.clearTimeout(pageNavCooldownTimerRef.current);
      }
    };
  }, []);

  const beginPageNavCooldown = useCallback(() => {
    if (pageNavCooldownTimerRef.current != null) {
      globalThis.clearTimeout(pageNavCooldownTimerRef.current);
    }
    pageNavCooldownRef.current = true;
    pageNavCooldownTimerRef.current = globalThis.setTimeout(() => {
      pageNavCooldownRef.current = false;
      pageNavCooldownTimerRef.current = null;
    }, PDF_PAGE_NAV_COOLDOWN_MS);
  }, []);

  useEffect(() => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const active = container.querySelectorAll("div[aria-label^='Page']")[
      currentPage - 1
    ] as HTMLElement;
    if (active) {
      const offsetLeft = active.offsetLeft - container.clientWidth / 2 + active.clientWidth / 2;
      container.scrollTo({ left: offsetLeft, behavior: 'smooth' });
    }
  }, [currentPage]);

  const handleDocumentLoad = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setCurrentPage((p) => Math.min(Math.max(1, p), numPages));
  };

  const goToPreviousPage = useCallback(() => {
    if (pageNavCooldownRef.current) return;
    const newPage = Math.max(1, currentPage - 1);
    if (newPage === currentPage) return;
    setCurrentPage(newPage);
    if (activeLiveSlide?.id && handleTriggerEvent) {
      handleTriggerEvent({
        eventType: 'PDF_PAGE_CHANGE',
        resourceType: 'WEBINAR_SLIDE',
        resourceId: activeLiveSlide.id,
        payloadJson: JSON.stringify({
          pageNumber: newPage,
          timeNow: Date.now(),
        }),
      });
    }
    beginPageNavCooldown();
  }, [beginPageNavCooldown, currentPage, activeLiveSlide?.id, handleTriggerEvent]);

  const goToNextPage = useCallback(() => {
    if (pageNavCooldownRef.current) return;
    if (numPages == null) return;
    const newPage = Math.min(numPages, currentPage + 1);
    if (newPage === currentPage) return;
    setCurrentPage(newPage);
    if (activeLiveSlide?.id && handleTriggerEvent) {
      handleTriggerEvent({
        eventType: 'PDF_PAGE_CHANGE',
        resourceType: 'WEBINAR_SLIDE',
        resourceId: activeLiveSlide.id,
        payloadJson: JSON.stringify({
          pageNumber: newPage,
          timeNow: Date.now(),
        }),
      });
    }
    beginPageNavCooldown();
  }, [beginPageNavCooldown, currentPage, numPages, activeLiveSlide?.id, handleTriggerEvent]);

  useEffect(() => {
    if (!displaySlide || !isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput =
        /^(INPUT|TEXTAREA|SELECT)$/i.test(target?.tagName) || target?.isContentEditable;
      if (isInput) return;
      if (pageNavCooldownRef.current) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPreviousPage();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNextPage();
      }
    };

    globalThis.addEventListener('keydown', handleKeyDown);
    return () => globalThis.removeEventListener('keydown', handleKeyDown);
  }, [displaySlide, isVisible, goToPreviousPage, goToNextPage]);

  const handlePageClick = useCallback(
    (pageNumber: number) => {
      if (pageNavCooldownRef.current) return;
      if (pageNumber === currentPage) return;
      setCurrentPage(pageNumber);
      if (activeLiveSlide?.id && handleTriggerEvent) {
        handleTriggerEvent({
          eventType: 'PDF_PAGE_CHANGE',
          resourceType: 'WEBINAR_SLIDE',
          resourceId: activeLiveSlide.id,
          payloadJson: JSON.stringify({
            pageNumber: pageNumber,
            timeNow: Date.now(),
          }),
        });
      }
      beginPageNavCooldown();
    },
    [beginPageNavCooldown, currentPage, activeLiveSlide?.id, handleTriggerEvent],
  );

  const hasStageCameras = Boolean(stageRects && stageRects.cams.length > 0);
  const stageFrame = fitBox(
    Math.max(0, stageArea.w - (hasStageCameras ? STAGE_CAM_CONTROLS_GUTTER_PX : 0)),
    stageArea.h,
    16 / 9,
  );
  const pageBlock = (
    <div
      className={
        fillStage
          ? 'h-full w-full overflow-hidden'
          : `mx-auto h-full overflow-hidden ${isSpeaker ? 'rounded-2xl' : 'rounded-t-2xl'} ${hasActiveTab ? 'rounded-t-none' : 'w-full'}`
      }
      style={
        fillStage
          ? undefined
          : pdfContainerWidth != null
            ? { width: pdfContainerWidth, maxWidth: '100%' }
            : { maxWidth: '100%' }
      }
    >
      <div className="relative h-full w-full">
        <Document
          file={toSlidesProxyUrl(displaySlide?.slideUrl)}
          className="h-full w-full"
          onLoadError={(e) => console.error(e)}
          onLoadSuccess={handleDocumentLoad}
          loading={<Skeleton className=" h-full rounded-t-2xl " />}
          error={<div className="p-4 text-sm text-red-600">{t('adminLive.pdf.loadError')}</div>}
          noData={<div className="p-4 text-sm text-gray-600">{t('adminLive.pdf.noData')}</div>}
        >
          <PdfSeamlessPage
            wrapperClassName="relative h-full w-full [&>canvas[data-pdf-snapshot]]:rounded-2xl"
            className={`flex justify-center items-center [&_canvas]:rounded-2xl ${isSpeaker ? '[&_canvas]:w-full! [&_canvas]:h-auto!' : '[&_canvas]:w-full! [&_canvas]:h-full! [&_canvas]:object-contain!'} bg-transparent! h-full`}
            pageNumber={currentPage}
            width={pdfPageWidth}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            onLoadSuccess={(page) => {
              const { width, height } = page.getViewport({ scale: 1 });
              if (!height) return;
              const aspect = width / height;
              setPdfPageAspect((previous) =>
                previous != null && Math.abs(previous - aspect) < 0.001 ? previous : aspect,
              );
            }}
          />
        </Document>
      </div>
    </div>
  );

  return (
    <div
      ref={containerRef}
      className="relative w-full min-w-0 mt-0 rounded-2xl overflow-hidden flex flex-col h-full justify-between"
      style={{
        backgroundColor: visualIdentity?.secondaryBackgroundColor + '20' || '#FFFFFF',
      }}
    >
      {fillStage ? (
        // Zone du haut : bloc 16:9 occupant la hauteur restante, contenu + cam repositionnés
        // comme le flux spectateur (même géométrie que le compositor). La barre de slides
        // (rendue plus bas) reste pleine largeur, hors de ce bloc.
        // `pl-11` : gouttière des contrôles cam, rendus hors cadre par la régie.
        <div
          ref={stageAreaRef}
          className="relative flex-1 min-h-0 flex items-center justify-center"
          style={{ paddingLeft: hasStageCameras ? STAGE_CAM_CONTROLS_GUTTER_PX : 0 }}
        >
          <div
            className="relative"
            style={
              stageFrame
                ? { width: stageFrame.width, height: stageFrame.height }
                : { width: '100%', aspectRatio: '16 / 9' }
            }
          >
            <div
              ref={pdfAreaRef}
              className="absolute flex items-center justify-center"
              style={
                stageRects?.content
                  ? pctRectStyle(stageRects.content)
                  : { left: 0, top: 0, width: '100%', height: '100%' }
              }
            >
              <div
                className={`relative overflow-hidden ${pdfFittedBox ? '' : 'h-full w-full'}`}
                style={{
                  borderRadius: scaleCamLayoutPx(
                    stageRects?.contentRadiusPx ?? 0,
                    stageFrame?.height,
                  ),
                  ...(pdfFittedBox
                    ? { width: pdfFittedBox.width, height: pdfFittedBox.height }
                    : undefined),
                }}
              >
                {pageBlock}
              </div>
            </div>
            {renderStageCameras?.(stageFrame?.height)}
          </div>
        </div>
      ) : (
        pageBlock
      )}

      {!isSpeaker && (
        <>
          {!isNavBarVisible && (
            <div
              className="absolute bottom-2 right-2 flex items-center gap-1 rounded-lg shadow-md overflow-hidden"
              style={{
                backgroundColor: visualIdentity?.theme === 'DARK' ? '#333' : '#FFFFFF',
                border: `1px solid ${visualIdentity?.theme === 'DARK' ? '#555' : '#DDE1E6'}`,
              }}
            >
              <button
                type="button"
                onClick={goToPreviousPage}
                className="p-2 transition-opacity hover:opacity-80 disabled:opacity-40"
                style={{ color: visualIdentity?.theme === 'DARK' ? '#FFFFFF' : '#000000' }}
                aria-label={t('adminLive.slides.previousPage')}
                disabled={currentPage <= 1}
              >
                <ArrowLeft size={18} />
              </button>
              <span
                className="text-sm font-medium px-2 py-2 min-w-[4rem] text-center"
                style={{ color: visualIdentity?.theme === 'DARK' ? '#FFFFFF' : '#000000' }}
              >
                {currentPage} / {numPages ?? '—'}
              </span>
              <button
                type="button"
                onClick={goToNextPage}
                className="p-2 transition-opacity hover:opacity-80 disabled:opacity-40"
                style={{ color: visualIdentity?.theme === 'DARK' ? '#FFFFFF' : '#000000' }}
                aria-label={t('adminLive.slides.nextPage')}
                disabled={numPages != null ? currentPage >= numPages : true}
              >
                <ArrowRight size={18} />
              </button>
              <button
                type="button"
                onClick={() => setIsNavBarVisible(true)}
                className="p-2 border-l transition-opacity hover:opacity-80"
                style={{
                  color: visualIdentity?.theme === 'DARK' ? '#FFFFFF' : '#000000',
                  borderColor: visualIdentity?.theme === 'DARK' ? '#555' : '#DDE1E6',
                }}
                aria-label={t('adminLive.slides.showNav')}
              >
                <ChevronUp size={18} />
              </button>
            </div>
          )}

          <div
            className={`overflow-hidden transition-all duration-200 ${
              isNavBarVisible ? 'max-h-[280px] min-h-fit opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div
              className="border relative border-[#DDE1E6] backdrop-blur-sm p-4 rounded-b-2xl flex flex-col"
              style={{
                boxShadow: '0 4px 6px 0 rgba(28, 43, 53, 0.08)',
                backgroundColor: visualIdentity?.theme === 'DARK' ? '#00000060' : '#FFFFFF60',
              }}
            >
              <button
                type="button"
                onClick={() => setIsNavBarVisible(false)}
                className="p-1.5 absolute top-0 right-0 z-50 rounded-md hover:bg-black/5 transition-colors"
                style={{
                  color: visualIdentity?.theme === 'DARK' ? '#FFFFFF' : '#000000',
                }}
                aria-label={t('adminLive.slides.hideNav')}
              >
                <ChevronDown size={20} />
              </button>

              {currentPageNote?.text && (
                <p
                  className={`text-sm mb-4 ${
                    visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'
                  }`}
                >
                  {currentPageNote.text}
                </p>
              )}

              <div className="flex items-center justify-between px-2 min-w-0">
                <ArrowLeft
                  onClick={currentPage <= 1 ? undefined : goToPreviousPage}
                  aria-label={t('adminLive.slides.previousPage')}
                  aria-disabled={currentPage <= 1}
                  className={currentPage <= 1 ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}
                  size={24}
                  color={visualIdentity?.theme === 'DARK' ? '#FFFFFF' : '#000000'}
                />
                {numPages ? (
                  <div
                    ref={scrollContainerRef}
                    className="flex-1 min-w-0 mx-2 overflow-x-auto"
                    style={{ scrollbarColor: 'currentColor transparent', scrollbarWidth: 'thin' }}
                  >
                    <Document
                      file={toSlidesProxyUrl(displaySlide?.slideUrl)}
                      onLoadError={(e) => console.error(e)}
                      loading={<div className="h-20" />}
                      error={
                        <div className="text-sm text-red-600">
                          {t('adminLive.slides.loadError')}
                        </div>
                      }
                      noData={
                        <div className="text-sm text-gray-600">{t('adminLive.slides.noData')}</div>
                      }
                    >
                      <div className="flex items-center gap-2 py-2">
                        {Array.from({ length: numPages }, (_, i) => i + 1).map((n) => (
                          <div
                            key={n}
                            className={[
                              'relative shrink-0 rounded-md overflow-hidden border-2 transition-colors ',
                              currentPage === n
                                ? 'border-blue-500'
                                : 'border-transparent cursor-pointer',
                            ].join(' ')}
                            aria-label={t('adminLive.pdf.page', { page: n })}
                            title={t('adminLive.pdf.page', { page: n })}
                            onClick={currentPage === n ? undefined : () => handlePageClick(n)}
                          >
                            <Page
                              pageNumber={n}
                              width={72}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                              loading={<Skeleton className="w-[72px] h-[102px]" />}
                            />
                            <span className="absolute bottom-1 right-1 text-[10px] px-1 py-0.5 bg-black/50 text-white rounded">
                              {n}
                            </span>
                          </div>
                        ))}
                      </div>
                    </Document>
                  </div>
                ) : (
                  <div className="text-sm text-gray-600">—</div>
                )}
                <ArrowRight
                  onClick={numPages == null || currentPage >= numPages ? undefined : goToNextPage}
                  aria-label={t('adminLive.slides.nextPage')}
                  aria-disabled={numPages == null || currentPage >= numPages}
                  className={
                    numPages == null || currentPage >= numPages
                      ? 'cursor-not-allowed opacity-40'
                      : 'cursor-pointer'
                  }
                  size={24}
                  color={visualIdentity?.theme === 'DARK' ? '#FFFFFF' : '#000000'}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
